import { useMemo, useState } from "react";

import { completeFleetOrderManually } from "../../api/client.js";
import { Dialog } from "../Dialog.jsx";
import { Icon } from "../Icon.jsx";

const MIN_REASON_LENGTH = 10;

function completionSummary(fleetOrder) {
  const policy =
    fleetOrder?.operationPolicy || {};
  const totalStops = Math.max(
    0,
    Number(
      policy.manualCompletionTotalStops ??
        fleetOrder?.stopCount ??
        0,
    ) || 0,
  );
  const remainingStops = Math.max(
    0,
    Number(
      policy.manualCompletionRemainingStops ??
        0,
    ) || 0,
  );

  return {
    totalStops,
    finalStops:
      Math.max(
        0,
        totalStops - remainingStops,
      ),
    remainingStops,
    operationalPhase:
      String(
        policy.operationalPhase ||
          "NOT_STARTED",
      )
        .trim()
        .toUpperCase(),
    ready:
      policy.canManualComplete === true,
    blockedReason:
      policy.manualCompletionReason ||
      "Kondisi penyelesaian belum dapat diverifikasi.",
  };
}

export function FleetOrderManualCompletionDialog({
  fleetOrder,
  onClose,
  onCompleted,
}) {
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const summary = useMemo(
    () => completionSummary(fleetOrder),
    [fleetOrder],
  );

  const reasonReady =
    reason.trim().length >= MIN_REASON_LENGTH;
  const canSubmit =
    summary.ready &&
    reasonReady &&
    confirmed &&
    !busy;

  async function submit() {
    if (!canSubmit) return;

    setBusy(true);
    setError("");

    try {
      const response =
        await completeFleetOrderManually(
          fleetOrder.id,
          {
            reason: reason.trim(),
            confirmManualCompletion:
              confirmed,
          },
        );

      onCompleted(response);
    } catch (requestError) {
      setError(
        requestError.message ||
          "Fleet Order belum dapat diselesaikan.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      className="dialog-card--fleet-manual-completion"
      onClose={() => !busy && onClose()}
      title="Selesaikan Fleet Order"
    >
      <div className="dialog-card__body fleet-manual-completion">
        <section className="fleet-manual-completion__intro">
          <Icon name="warning" size={18} />
          <div>
            <strong>{fleetOrder.foNumber}</strong>
            <span>
              Gunakan tindakan ini setelah setiap titik memiliki hasil akhir.
              Status stop, bukti, dan Delivery Order tidak akan diubah.
            </span>
          </div>
        </section>

        <section className="fleet-manual-completion__summary">
          <div>
            <span>Total titik</span>
            <strong>{summary.totalStops}</strong>
          </div>
          <div>
            <span>Hasil akhir</span>
            <strong>{summary.finalStops}</strong>
          </div>
          <div>
            <span>Belum selesai</span>
            <strong>{summary.remainingStops}</strong>
          </div>
          <div>
            <span>Tahap eksekusi</span>
            <strong>{summary.operationalPhase}</strong>
          </div>
        </section>

        {!summary.ready && (
          <div className="warning-banner" role="status">
            <strong>Belum dapat diselesaikan</strong>
            <span>{summary.blockedReason}</span>
          </div>
        )}

        {error && (
          <div className="error-banner" role="alert">
            {error}
          </div>
        )}

        <section className="fleet-manual-completion__form">
          <label>
            <span>Alasan penyelesaian</span>
            <textarea
              autoFocus
              maxLength={500}
              minLength={MIN_REASON_LENGTH}
              onChange={(event) => {
                setReason(event.target.value);
                setConfirmed(false);
              }}
              placeholder="Jelaskan alasan Fleet Order diselesaikan melalui admin."
              rows={4}
              value={reason}
            />
            <small>
              {reason.trim().length}/500 · minimal {MIN_REASON_LENGTH} karakter
            </small>
          </label>

          <label className="fleet-manual-completion__confirmation">
            <input
              checked={confirmed}
              disabled={!summary.ready || !reasonReady}
              onChange={(event) =>
                setConfirmed(event.target.checked)
              }
              type="checkbox"
            />
            <span>
              <strong>
                Saya sudah memastikan seluruh titik memiliki hasil akhir.
              </strong>
              <small>
                Fleet Order akan ditutup. Status tiap titik dan seluruh
                bukti operasional tetap dipertahankan.
              </small>
            </span>
          </label>
        </section>
      </div>

      <footer className="dialog-card__actions fleet-manual-completion__actions">
        <button
          className="secondary-button"
          disabled={busy}
          onClick={onClose}
          type="button"
        >
          Batal
        </button>
        <button
          className="primary-button"
          disabled={!canSubmit}
          onClick={submit}
          type="button"
        >
          {busy ? "Menyelesaikan..." : "Selesaikan Fleet Order"}
        </button>
      </footer>
    </Dialog>
  );
}
